import { Injector, inject, runInInjectionContext } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router';

import { Capacitor } from '@capacitor/core';

import { filter, firstValueFrom, take } from 'rxjs';

import { AUTH_ROUTE_PATHS } from './auth-route-paths.token';
import { AUTH_SESSION } from './auth-session.token';
import { canAny } from './permissions';
import { AuthSession, AuthStatus, Permission } from './session.types';

type ResolvedStatus = Exclude<AuthStatus, 'unknown'>;

function resolveStatus(
  session: AuthSession,
  injector: Injector
): Promise<ResolvedStatus> {
  const current = session.status();
  if (current !== 'unknown') {
    return Promise.resolve(current);
  }
  return runInInjectionContext(injector, () =>
    firstValueFrom(
      toObservable(session.status).pipe(
        filter((status): status is ResolvedStatus => status !== 'unknown'),
        take(1)
      )
    )
  );
}

export const sessionGuard: CanActivateFn = async (
  _route,
  state
): Promise<boolean | UrlTree> => {
  const session = inject(AUTH_SESSION);
  const router = inject(Router);
  const paths = inject(AUTH_ROUTE_PATHS);
  const injector = inject(Injector);

  const status = await resolveStatus(session, injector);
  if (status === 'authenticated') {
    return true;
  }
  return router.createUrlTree([paths.login], {
    queryParams: { returnUrl: state.url },
  });
};

export const redirectIfAuthenticatedGuard: CanActivateFn = async (
  route
): Promise<boolean | UrlTree> => {
  const session = inject(AUTH_SESSION);
  const router = inject(Router);
  const paths = inject(AUTH_ROUTE_PATHS);
  const injector = inject(Injector);

  const status = await resolveStatus(session, injector);
  if (status !== 'authenticated') {
    return true;
  }
  return router.parseUrl(route.queryParamMap.get('returnUrl') ?? paths.home);
};

export function permissionGuard(
  permission: Permission | ReadonlyArray<Permission>
): CanMatchFn {
  return async (): Promise<boolean | UrlTree> => {
    const session = inject(AUTH_SESSION);
    const router = inject(Router);
    const paths = inject(AUTH_ROUTE_PATHS);
    const injector = inject(Injector);

    const status = await resolveStatus(session, injector);
    if (status !== 'authenticated') {
      return router.createUrlTree([paths.login]);
    }

    const required = Array.isArray(permission)
      ? permission
      : [permission as Permission];
    if (canAny(session, required)) {
      return true;
    }
    return router.createUrlTree([paths.forbidden]);
  };
}

export function permissionCanActivate(
  permission: Permission | ReadonlyArray<Permission>
): CanActivateFn {
  return async (): Promise<boolean | UrlTree> => {
    const session = inject(AUTH_SESSION);
    const router = inject(Router);
    const paths = inject(AUTH_ROUTE_PATHS);
    const injector = inject(Injector);

    const status = await resolveStatus(session, injector);
    if (status !== 'authenticated') {
      return router.createUrlTree([paths.login]);
    }

    const required = Array.isArray(permission)
      ? permission
      : [permission as Permission];
    if (canAny(session, required)) {
      return true;
    }
    return router.createUrlTree([paths.forbidden]);
  };
}

export function nativeOnlyGuard(): CanMatchFn {
  return (): boolean => Capacitor.isNativePlatform();
}
